import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { showAlert } from '@/utils/common/alert';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { 
  PaymentSectionHeader,
  PaymentRadioOption,
  PaymentAmountInput,
  ConvenienceFeesSection,
  PaymentSectionDivider,
} from '@/components/residents/payments/shared';
import { useAppSelector, useAppDispatch } from '@/store';
import { PaymentMethodSelector } from '@/components/residents/payments/PaymentMethodSelector';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import {
  useSetupAutoPayMutation,
  leasesApi,
} from '@/store/api';
import { sanitizeCurrencyInput, getFirstOfNextMonth } from '@/utils/common/formatting';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';

type Params = {
  leaseId: number;
  residentId?: number;
  fromTransactionReceived?: boolean;
};

export enum AUTOPAY_WIZARD_STEPS {
  SETTINGS = 0,        // Amount, Date, Payment Method all in one (like web)
  PROCESSING = 1,      // Processing step  
  SUCCESS = 2,         // Success step
}

export const AutoPayWizardScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const { colors, semantic } = useThemeColors();
  const styles = useAutopayStyles();

  const dispatch = useAppDispatch();
  const { leaseId, residentId, fromTransactionReceived } = (route.params || {}) as Params;
  const [currentStep, setCurrentStep] = useState<AUTOPAY_WIZARD_STEPS>(AUTOPAY_WIZARD_STEPS.SETTINGS);

  const authUser = useAppSelector((s) => (s as any).auth?.user);
  
  // Form state - matching web app structure
  const [amountOption, setAmountOption] = useState<'full' | 'custom'>('full');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [dateRangeOption, setDateRangeOption] = useState<'default' | 'custom'>('default');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  
  const [setupAutoPay, { isLoading: isSettingUp }] = useSetupAutoPayMutation();

  // Get payment data - reuse existing hook
  const paymentData = usePaymentData(residentId);
  const { 
    paymentMethods, 
    balanceDue, 
    feeStructureDisplay,
    selectedPaymentMethod: currentSelectedMethod,
    updateSelectedPaymentMethod,
  } = paymentData;

  const property = useAppSelector((s) => (s as any).property?.current);
  const minPaymentAmount = Number(property?.minPaymentAmount ?? 10);

  // Initialize selected payment method from existing payment data
  useEffect(() => {
    if (currentSelectedMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(currentSelectedMethod);
    } else if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      const firstMethod = paymentMethods[0];
      setSelectedPaymentMethod(firstMethod);
      updateSelectedPaymentMethod(firstMethod);
    }
  }, [paymentMethods, selectedPaymentMethod, currentSelectedMethod, updateSelectedPaymentMethod]);

  // Initialize custom amount - matching Payment Summary logic
  useEffect(() => {
    if (!customAmount) {
      const fallbackMin = minPaymentAmount;
      const canPayBalanceUnderMin = balanceDue > 0 && balanceDue < minPaymentAmount;
      const defaultCustom = canPayBalanceUnderMin && balanceDue > 0 ? balanceDue : fallbackMin;
      const formattedAmount = String(Number.isFinite(defaultCustom) ? defaultCustom.toFixed(2) : '');
      setCustomAmount(formattedAmount);
    }
  }, [balanceDue, customAmount, minPaymentAmount]);

  // Handle amount option changes - matching Payment Summary logic
  const handleFullBalanceAmount = () => {
    setAmountOption('full');
  };

  const handleOtherAmount = () => {
    setAmountOption('custom');
  };

  // Handle custom amount text changes
  const handleCustomAmountChange = (text: string) => {
    const cleanedText = sanitizeCurrencyInput(text);
    setCustomAmount(cleanedText);
  };

  const handleSetupAutoPay = async () => {
    if (!selectedPaymentMethod) return;
    
    setCurrentStep(AUTOPAY_WIZARD_STEPS.PROCESSING);
    
    try {
      const setupData = {
        leaseId,
        sourceId: Number(selectedPaymentMethod.sourceId || selectedPaymentMethod.id),
        residentId: residentId && residentId !== authUser?.id ? residentId : undefined,
        autopayIsFullAmount: amountOption === 'full',
        autopayIsLeaseLifetime: dateRangeOption === 'default',
        ...(amountOption === 'custom' && { fixedAmount: Number(customAmount) }),
      };

      await setupAutoPay(setupData).unwrap();
      dispatch(leasesApi.util.resetApiState());
      setCurrentStep(AUTOPAY_WIZARD_STEPS.SUCCESS);
    } catch (error) {
      console.error('AutoPay setup error:', error);
      showAlert('Error', 'Failed to set up AutoPay. Please try again.');
      setCurrentStep(AUTOPAY_WIZARD_STEPS.SETTINGS);
    }
  };

  const handleClose = () => {
    if (fromTransactionReceived) {
      // Go back to payments screen after coming from transaction received
      navigation.replace('Payments');
    } else {
      navigation.goBack();
    }
  };

  const canProceedToPaymentMethod = () => {
    if (amountOption === 'custom') {
      const amount = Number(customAmount);
      return amount >= minPaymentAmount;
    }
    return true;
  };

  const canProceedToConfirmation = () => {
    return selectedPaymentMethod !== null;
  };

  // getFirstOfNextMonth now provided by shared utils

  // Format date range text to match web app exactly
  const getDateRangeText = () => {
    const now = new Date();
    const firstOfNextMonth = getFirstOfNextMonth(now);
    const formattedDate = firstOfNextMonth.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit", 
      year: "numeric",
    });
    return `From the first of the month (${formattedDate}) to lease end`;
  };

  // Step 1: AutoPay Settings (Amount, Date Range, Payment Method) - matching web app
  const renderSettingsStep = () => (
    <>
      <View style={styles.section}>
        <PaymentSectionHeader
          title="Amount"
          tooltipText="There is a limit of one autopay per user per lease. The full balance autopay will not process a payment if you have a credit on your account or if your balance owed is equal to $0. Custom amounts will process the specified amount if the balance owed is greater than $0 and will result a credit on your account if the amount is greater than the balance owed."
        />

        <PaymentRadioOption
          selected={amountOption === 'full'}
          onPress={handleFullBalanceAmount}
          label="Full balance"
        />

        <PaymentRadioOption
          selected={amountOption === 'custom'}
          onPress={handleOtherAmount}
          label="Other amount"
          rightComponent={
            <PaymentAmountInput
              value={customAmount}
              onChangeText={handleCustomAmountChange}
              placeholder="10.00"
              editable={amountOption === 'custom'}
              disabled={amountOption !== 'custom'}
            />
          }
        />
      </View>

      <PaymentSectionDivider />

      <View style={styles.section}>
        <PaymentSectionHeader
          title="Date range"
          tooltipText="Your payments will be processed on the first day of each month until your lease ends."
        />

        <PaymentRadioOption
          selected={dateRangeOption === 'default'}
          onPress={() => setDateRangeOption('default')}
          label={getDateRangeText()}
        />
      </View>

      <PaymentSectionDivider />

      {/* Payment Method Section - matching web app structure */}
      <PaymentMethodSelector
        paymentMethods={paymentMethods}
        selectedMethod={selectedPaymentMethod || currentSelectedMethod}
        onSelectMethod={(method) => {
          setSelectedPaymentMethod(method);
          updateSelectedPaymentMethod(method);
        }}
        onManagePaymentMethods={() => {
          // Match PaymentSummaryModal navigation pattern
          navigation.replace('AddOrChangePaymentMethod', {
            leaseId,
            residentId,
            returnTo: 'AutoPayWizard',
            successPage: 'AutoPayWizard',
            managePaymentPage: 'ManageMyWallet'
          });
        }}
        isLoading={false}
        showChangeButton={true}
        maxHeight={240}
      />

      <PaymentSectionDivider />

      <ConvenienceFeesSection feeStructure={feeStructureDisplay} />
    </>
  );


  // Step 2: Processing
  const renderProcessingStep = () => (
    <View style={styles.centerContainer}>
      <LoadingIcon />
      <Text style={styles.processingTitle}>Setting up AutoPay...</Text>
      <Text style={styles.processingMessage}>
        Please wait while we configure your recurring payment.
      </Text>
    </View>
  );

  // Step 3: Success
  const renderSuccessStep = () => (
    <View style={styles.centerContainer}>
      <SuccessIcon />
      <Text style={styles.successTitle}>AutoPay Set Up Successfully!</Text>
      <Text style={styles.successMessage}>
        Your recurring payment has been configured. You'll be charged automatically 
        on the first of each month.
      </Text>
    </View>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case AUTOPAY_WIZARD_STEPS.SETTINGS:
        return 'Autopay settings';
      case AUTOPAY_WIZARD_STEPS.PROCESSING:
        return 'Setting up AutoPay';
      case AUTOPAY_WIZARD_STEPS.SUCCESS:
        return 'AutoPay configured';
      default:
        return 'AutoPay Setup';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case AUTOPAY_WIZARD_STEPS.SETTINGS:
        return renderSettingsStep();
      case AUTOPAY_WIZARD_STEPS.PROCESSING:
        return renderProcessingStep();
      case AUTOPAY_WIZARD_STEPS.SUCCESS:
        return renderSuccessStep();
      default:
        return null;
    }
  };

  const getFooterElement = () => {
    if (currentStep === AUTOPAY_WIZARD_STEPS.PROCESSING) {
      return undefined;
    }

    if (currentStep === AUTOPAY_WIZARD_STEPS.SUCCESS) {
      return (
        <View style={styles.footerRow}>
          <ModalButton label="Close" variant="primary" onPress={handleClose} />
        </View>
      );
    }

    return (
      <View style={styles.footerRow}>
        <ModalButton label="Cancel" variant="secondary" onPress={handleClose} />
        <ModalButton
          label="Confirm"
          variant="primary"
          onPress={handleSetupAutoPay}
          disabled={!canProceedToPaymentMethod() || !canProceedToConfirmation() || isSettingUp}
        />
      </View>
    );
  };

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="large"
      onClose={handleClose}
      title={getStepTitle()}
      scrollable
      dismissOnBackdrop={currentStep !== AUTOPAY_WIZARD_STEPS.PROCESSING}
      footer={getFooterElement()}
    >
      {renderStepContent()}
    </UnifiedModal>
  );
};

// Helper Icons
const SuccessIcon: React.FC = () => {
  const { colors, semantic } = useThemeColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHECK_CIRCLE;
  return Icon ? (
    <Icon width={64} height={64} color={colors.accent.green} />
  ) : (
    <View style={[staticStyles.successIconFallback, { backgroundColor: colors.accent.green }]}>
      <Text style={[staticStyles.successIconText, { color: semantic.text.inverse }]}>✓</Text>
    </View>
  );
};

const LoadingIcon: React.FC = () => {
  const { semantic } = useThemeColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).LOADING_SPINNER;
  return Icon ? (
    <Icon width={48} height={48} color={semantic.interactive.primary} />
  ) : (
    <View style={[staticStyles.loadingIconFallback, { backgroundColor: semantic.interactive.primary }]}>
      <Text style={[staticStyles.loadingIconText, { color: semantic.text.inverse }]}>⟳</Text>
    </View>
  );
};

// Static styles used by sub-components (SuccessIcon, LoadingIcon) that have their own useThemeColors
const staticStyles = StyleSheet.create({
  successIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIconText: {
    fontSize: 32,
    fontWeight: 'bold',
  },
  loadingIconFallback: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingIconText: {
    fontSize: 24,
    fontWeight: 'bold',
  },
});

const useAutopayStyles = () =>
  useThemedStyles(({ colors }) => ({
    centerContainer: {
      flex: 1,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      paddingVertical: 48,
    },
    section: {
      paddingVertical: 14,
    },
    processingTitle: {
      fontSize: 24,
      fontWeight: '700' as const,
      color: colors.text.primary,
      marginTop: 24,
      marginBottom: 8,
    },
    processingMessage: {
      fontSize: 16,
      color: colors.text.muted,
      textAlign: 'center' as const,
    },
    successTitle: {
      fontSize: 24,
      fontWeight: '700' as const,
      color: colors.text.primary,
      marginTop: 24,
      marginBottom: 8,
      textAlign: 'center' as const,
    },
    successMessage: {
      fontSize: 16,
      color: colors.text.muted,
      textAlign: 'center' as const,
      lineHeight: 24,
    },
    footerRow: {
      flexDirection: 'row' as const,
      gap: 12,
    },
  }));

export default AutoPayWizardScreen;